import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import axios from 'axios';

function WhoisLookup() {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = async () => {
    if (!domain) {
      setError('Please enter a domain name');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Using a public WHOIS API
      const response = await axios.get(`https://api.whoisjson.com/v1/${domain}`);
      
      if (response.data.error) {
        setError(response.data.error);
        return;
      }

      setResult({
        domain: domain,
        registrar: response.data.registrar || 'N/A',
        registrant: response.data.registrant || 'N/A',
        created: response.data.created || 'N/A',
        updated: response.data.updated || 'N/A',
        expires: response.data.expires || 'N/A',
        nameservers: response.data.nameservers || [],
        status: response.data.status || 'N/A',
        country: response.data.country || 'N/A',
        organization: response.data.organization || 'N/A'
      });
    } catch (err) {
      // Fallback to alternative API or mock data
      try {
        const fallbackResponse = await axios.get(`https://api.hackertarget.com/whois/?q=${domain}`);
        
        // Parse the text response
        const whoisText = fallbackResponse.data;
        const lines = whoisText.split('\n');
        
        const parseField = (fieldName) => {
          const line = lines.find(l => l.toLowerCase().includes(fieldName.toLowerCase()));
          return line ? line.split(':')[1]?.trim() : 'N/A';
        };

        setResult({
          domain: domain,
          registrar: parseField('registrar') || parseField('sponsoring registrar'),
          created: parseField('creation date') || parseField('created'),
          updated: parseField('updated date') || parseField('last updated'),
          expires: parseField('expiry date') || parseField('expires'),
          nameservers: lines.filter(l => l.toLowerCase().includes('name server')).map(l => l.split(':')[1]?.trim()).filter(Boolean),
          status: parseField('status'),
          country: parseField('country'),
          organization: parseField('organization') || parseField('org'),
          rawData: whoisText
        });
      } catch {
        setError('Unable to retrieve WHOIS information for this domain');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        WHOIS Lookup
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Domain Name"
              variant="outlined"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
            />
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleLookup}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Lookup WHOIS'}
            </Button>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Card sx={{ 
                mt: 2, 
                p: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 2
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  WHOIS Information
                </Typography>
                
                <TableContainer component={Paper} sx={{ bgcolor: 'background.default' }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                        <TableCell>{result.domain}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Registrar</TableCell>
                        <TableCell>{result.registrar}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                        <TableCell>{result.organization}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                        <TableCell>{result.country}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell>{result.created}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                        <TableCell>{result.updated}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                        <TableCell>{result.expires}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell>{result.status}</TableCell>
                      </TableRow>
                      {result.nameservers && result.nameservers.length > 0 && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Name Servers</TableCell>
                          <TableCell>
                            {result.nameservers.map((ns, index) => (
                              <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {ns}
                              </Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default WhoisLookup;
